import type { ReactNode } from 'react';
import { create } from 'zustand';
import type { IconName } from '../components/ui/Icon';

/**
 * Sistema di conferma imperativo, brand-coerente, che rimpiazza i confirm()/prompt()
 * grigi del browser. Tre livelli di attrito che scalano con l'irreversibilità:
 *  - confirm semplice           → un click
 *  - { danger: true }           → bottone rosso (azione distruttiva)
 *  - { requireType: 'ELIMINA' } → l'utente deve digitare la stringa per abilitare
 *
 * Uso:
 *   import { confirm } from '../../stores/confirm.store';
 *   if (await confirm({ title: 'Eliminare il task?', danger: true })) del.mutate();
 */
export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Stile distruttivo (bottone rosso). */
  danger?: boolean;
  /** Se presente, l'utente deve digitare ESATTAMENTE questa stringa per confermare. */
  requireType?: string;
  /** Icona opzionale mostrata nell'header del dialog. */
  icon?: IconName;
}

interface ConfirmState {
  open: boolean;
  closing: boolean;
  opts: ConfirmOptions | null;
  resolve: ((ok: boolean) => void) | null;
  request: (opts: ConfirmOptions) => Promise<boolean>;
  settle: (ok: boolean) => void;
}

const EXIT_MS = 160;

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  open: false,
  closing: false,
  opts: null,
  resolve: null,
  request: (opts) =>
    new Promise<boolean>((resolve) => {
      // Se ne arriva un'altra mentre una è aperta, risolvi la precedente a false.
      const prev = get().resolve;
      if (prev) prev(false);
      set({ open: true, closing: false, opts, resolve });
    }),
  settle: (ok) => {
    const { resolve } = get();
    resolve?.(ok);
    set({ closing: true, resolve: null });
    setTimeout(() => set({ open: false, closing: false, opts: null }), EXIT_MS);
  },
}));

/** Helper imperativo: restituisce true se l'utente conferma. */
export function confirm(opts: ConfirmOptions): Promise<boolean> {
  return useConfirmStore.getState().request(opts);
}
